import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import axiosInstance from "../../utils/axios";

interface InterestRow {
  id: number;
  productKey: string;
  quantityEstimate: string | null;
  notes: string | null;
  createdAt: string;
  association?: { id: number; name: string };
  user?: { id: number; firstName?: string; lastName?: string };
}

const PRODUCT_LABELS: Record<string, string> = {
  aurausviitta: "Aurausviitat",
  murske: "Murske",
  liikennemerkki: "Liikennemerkit",
  muu: "Muu",
};

export default function SuperadminProcurementInterests() {
  const [rows, setRows] = useState<InterestRow[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get<InterestRow[]>("/procurement-interests")
      .then((res) => {
        if (!cancelled) setRows(res.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const counts = (rows ?? []).reduce<Record<string, number>>((acc, row) => {
    acc[row.productKey] = (acc[row.productKey] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <Helmet title="Hankintakysyntä" />
      <Typography variant="h3" gutterBottom>
        Hankintakysyntä
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Tiekuntien ilmoittama kiinnostus yhteishankintoihin.
      </Typography>
      <Divider sx={{ my: 4 }} />

      {error && (
        <Alert severity="error">Kysynnän haku epäonnistui.</Alert>
      )}
      {!error && rows === null && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}
      {rows !== null && (
        <>
          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 4 }}>
            {Object.keys(PRODUCT_LABELS).map((key) => (
              <Chip
                key={key}
                label={`${PRODUCT_LABELS[key]}: ${counts[key] ?? 0}`}
                color={counts[key] ? "primary" : "default"}
                variant={counts[key] ? "filled" : "outlined"}
              />
            ))}
          </Stack>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tuote</TableCell>
                  <TableCell>Tiekunta</TableCell>
                  <TableCell>Määräarvio</TableCell>
                  <TableCell>Lisätiedot</TableCell>
                  <TableCell>Ilmoittaja</TableCell>
                  <TableCell>Pvm</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Ei ilmoituksia vielä.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        {PRODUCT_LABELS[row.productKey] ?? row.productKey}
                      </TableCell>
                      <TableCell>{row.association?.name ?? "-"}</TableCell>
                      <TableCell>{row.quantityEstimate ?? "-"}</TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        {row.notes ?? "-"}
                      </TableCell>
                      <TableCell>
                        {[row.user?.firstName, row.user?.lastName]
                          .filter(Boolean)
                          .join(" ") || "-"}
                      </TableCell>
                      <TableCell>
                        {new Date(row.createdAt).toLocaleDateString("fi-FI")}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </>
  );
}
